import type { StencilWizardPlugin, WizardContext } from '@stencil/cli';
import { Project, SyntaxKind } from 'ts-morph';
import { access, mkdir, writeFile } from 'node:fs/promises';
import { join, relative, dirname } from 'node:path';

// ---------------------------------------------------------------------------
// ts-morph helpers
// ---------------------------------------------------------------------------

function amendStencilConfig(configPath: string, targetCode: string, enableSsr: boolean): boolean {
  const project = new Project({ skipAddingFilesFromTsConfig: true });
  const src = project.addSourceFileAtPath(configPath);

  if (!src.getImportDeclaration('@stencil/react-output-target')) {
    src.addImportDeclaration({ moduleSpecifier: '@stencil/react-output-target', namedImports: ['reactOutputTarget'] });
  }

  const existingProp = src
    .getDescendantsOfKind(SyntaxKind.PropertyAssignment)
    .find((p) => p.getName() === 'outputTargets');

  if (!existingProp) {
    const configObj =
      src.getVariableDeclaration('config')?.getInitializerIfKind(SyntaxKind.ObjectLiteralExpression) ??
      src
        .getDescendantsOfKind(SyntaxKind.ObjectLiteralExpression)
        .find((obj) => obj.getProperty('namespace') !== undefined);
    if (!configObj) throw new Error('Could not find Stencil config object in stencil.config.ts');
    configObj.addPropertyAssignment({ name: 'outputTargets', initializer: '[]' });
  }

  const prop = src.getDescendantsOfKind(SyntaxKind.PropertyAssignment).find((p) => p.getName() === 'outputTargets')!;
  const arr = prop.getInitializerIfKind(SyntaxKind.ArrayLiteralExpression);
  if (!arr) throw new Error('outputTargets is not an array literal in stencil.config.ts');

  const existing = arr.getText();
  const alreadyAdded = existing.includes('reactOutputTarget(');

  // Filter out any existing reactOutputTarget call so it can be replaced cleanly
  const elements = arr
    .getElements()
    .map((e) => e.getText().trim())
    .filter((e) => !e.includes('reactOutputTarget('));

  const hasStandalone =
    existing.includes("type: 'standalone'") ||
    existing.includes('type: "standalone"') ||
    existing.includes("type: 'dist-custom-elements'") ||
    existing.includes('type: "dist-custom-elements"');
  if (!hasStandalone) elements.push("{ type: 'standalone' }");

  if (enableSsr) {
    const hasSSR =
      existing.includes("type: 'ssr'") ||
      existing.includes('type: "ssr"') ||
      existing.includes("type: 'dist-hydrate-script'") ||
      existing.includes('type: "dist-hydrate-script"');
    if (!hasSSR) elements.push("{ type: 'ssr' }");
  }

  elements.push(targetCode);

  // Replace the entire initializer as an explicitly multiline array so
  // formatText() sees a fresh structure and formats all elements consistently.
  prop.setInitializer(`[\n${elements.map((e) => `  ${e}`).join(',\n')},\n]`);
  src.formatText();
  src.saveSync();

  return !alreadyAdded;
}

// ---------------------------------------------------------------------------
// Wrapper package scaffolding
// ---------------------------------------------------------------------------

async function pathExists(p: string): Promise<boolean> {
  try {
    await access(p);
    return true;
  } catch {
    return false;
  }
}

async function scaffoldWrapperPackage(
  wrapperDir: string,
  packageName: string,
  corePackageName: string,
  corePkgVersion: string
): Promise<void> {
  await mkdir(join(wrapperDir, 'src'), { recursive: true });

  const pkgJson = {
    name: packageName,
    version: '0.0.1',
    type: 'module',
    main: './dist/index.js',
    types: './dist/index.d.ts',
    exports: { '.': { types: './dist/index.d.ts', import: './dist/index.js' } },
    scripts: { build: 'tsc', dev: 'tsc --watch' },
    peerDependencies: {
      react: '^18 || ^19',
      'react-dom': '^18 || ^19',
    },
    dependencies: { [corePackageName]: corePkgVersion },
    devDependencies: { typescript: '>=4 <7' },
  };

  const tsConfig = {
    compilerOptions: {
      target: 'ES2022',
      module: 'esnext',
      moduleResolution: 'bundler',
      declaration: true,
      rootDir: 'src',
      outDir: 'dist',
      strict: true,
      skipLibCheck: true,
    },
    include: ['src/**/*.ts', 'src/**/*.tsx'],
  };

  // components.ts lives at src/components.ts — generated by stencil build
  const indexTs =
    `// Re-exports React wrappers generated by @stencil/react-output-target.\n` +
    `// src/components.ts is populated by \`stencil build\` — do not edit manually.\n` +
    `export * from './components.js';\n`;

  await Promise.all([
    writeFile(join(wrapperDir, '.gitignore'), 'dist/\nnode_modules/\n', 'utf8'),
    writeFile(join(wrapperDir, 'package.json'), JSON.stringify(pkgJson, null, 2) + '\n', 'utf8'),
    writeFile(join(wrapperDir, 'tsconfig.json'), JSON.stringify(tsConfig, null, 2) + '\n', 'utf8'),
    writeFile(join(wrapperDir, 'src', 'index.ts'), indexTs, 'utf8'),
  ]);
}

// ---------------------------------------------------------------------------
// Wizard
// ---------------------------------------------------------------------------

export const wizard = {
  init: {
    id: '@stencil/react-output-target',
    displayName: 'React',
    description: 'Type-safe React wrappers for your Stencil components',

    async run({ config, workspaceRoot, prompts, nypm }: WizardContext): Promise<void> {
      const { intro, outro, text, confirm, spinner, isCancel, cancel, log } = prompts;

      intro('React output target');

      const stencilConfigPath = join(config.rootDir, 'stencil.config.ts');

      // Guard: already configured?
      const guardProject = new Project({ skipAddingFilesFromTsConfig: true });
      const alreadyConfigured = guardProject
        .addSourceFileAtPath(stencilConfigPath)
        .getDescendantsOfKind(SyntaxKind.PropertyAssignment)
        .find((p) => p.getName() === 'outputTargets')
        ?.getInitializerIfKind(SyntaxKind.ArrayLiteralExpression)
        ?.getText()
        .includes('reactOutputTarget(');

      if (alreadyConfigured) {
        const redo = await confirm({
          message: 'reactOutputTarget is already in stencil.config.ts — reconfigure?',
          initialValue: false,
        });
        if (isCancel(redo) || !redo) {
          cancel('Skipping React setup.');
          return;
        }
      }

      // Where should the wrapper package live?
      let wrapperDir: string;
      let wrapperPackageName = `${config.fsNamespace}-react`;

      if (workspaceRoot) {
        const rawName = await text({
          message: 'Wrapper package name?',
          placeholder: wrapperPackageName,
          defaultValue: wrapperPackageName,
        });
        if (isCancel(rawName)) {
          cancel('Setup cancelled.');
          return;
        }
        wrapperPackageName = rawName as string;
        wrapperDir = join(dirname(config.rootDir), wrapperPackageName);
      } else {
        const defaultRel = `../${config.fsNamespace}-react`;
        const rawDir = await text({
          message: 'Wrapper package directory? (relative to stencil.config.ts)',
          placeholder: defaultRel,
          defaultValue: defaultRel,
        });
        if (isCancel(rawDir)) {
          cancel('Setup cancelled.');
          return;
        }
        wrapperDir = join(config.rootDir, rawDir as string);
      }

      // Derive the stencil.config-relative outDir from the wrapper location
      const outDir = relative(config.rootDir, join(wrapperDir, 'src')).replace(/\\/g, '/');

      // SSR
      const enableSsr = await confirm({ message: 'Enable server-side rendering (SSR)?', initialValue: false });
      if (isCancel(enableSsr)) {
        cancel('Setup cancelled.');
        return;
      }

      // When not in a known monorepo, ask for the npm package name separately if SSR needs it
      if (enableSsr && !workspaceRoot) {
        const rawPkgName = await text({
          message: 'React wrapper npm package name? (used as clientModule in the SSR config)',
          placeholder: wrapperPackageName,
          defaultValue: wrapperPackageName,
        });
        if (isCancel(rawPkgName)) {
          cancel('Setup cancelled.');
          return;
        }
        wrapperPackageName = rawPkgName as string;
      }

      // Scaffold wrapper package if the directory doesn't exist yet
      const shouldScaffold = !(await pathExists(wrapperDir));
      if (shouldScaffold) {
        const s = spinner();
        s.start(`Scaffolding wrapper package at ${relative(workspaceRoot ?? config.rootDir, wrapperDir)}`);
        try {
          const corePkgVersion = workspaceRoot
            ? 'workspace:*'
            : `file:${relative(wrapperDir, config.rootDir).replace(/\\/g, '/')}`;
          await scaffoldWrapperPackage(wrapperDir, wrapperPackageName, config.fsNamespace, corePkgVersion);
          s.stop('Wrapper package scaffolded');
        } catch (e) {
          s.stop('Scaffolding failed — continuing');
          log.warn(`Could not scaffold wrapper package: ${e}`);
        }
      }

      // Build the output target code
      const targetCode = enableSsr
        ? `reactOutputTarget({\n    outDir: '${outDir}',\n    hydrateModule: '${config.fsNamespace}/dist/ssr',\n    clientModule: '${wrapperPackageName}',\n  })`
        : `reactOutputTarget({ outDir: '${outDir}' })`;

      // Amend stencil.config.ts in one pass so formatText() sees the final array
      try {
        const added = amendStencilConfig(stencilConfigPath, targetCode, enableSsr as boolean);
        log.success(added ? 'stencil.config.ts updated' : 'reactOutputTarget already present — no changes made');
      } catch (e) {
        log.warn(
          `Could not automatically update stencil.config.ts (${e}). Add manually:\n\n` +
            `import { reactOutputTarget } from '@stencil/react-output-target';\n` +
            `// in outputTargets:\n${targetCode}` +
            (enableSsr ? `\n{ type: 'ssr' }` : '')
        );
      }

      // Install @stencil/react-output-target as a devDep of the core package
      log.info('Installing @stencil/react-output-target...');
      await nypm.addDependency(['@stencil/react-output-target'], { cwd: config.rootDir, dev: true });
      log.success('Installed @stencil/react-output-target');

      // Install React peer deps in the wrapper package
      if (shouldScaffold && (await pathExists(wrapperDir))) {
        log.info('Installing React peer dependencies in wrapper package...');
        await nypm.addDependency(['@stencil/react-output-target'], { cwd: wrapperDir, dev: true });
        await nypm.addDependency(['react', 'react-dom', '@types/react', '@types/react-dom', 'typescript'], {
          cwd: wrapperDir,
          dev: true,
        });
        log.success('React peer dependencies installed');
      }

      outro('React output target configured');
    },
  },
} satisfies StencilWizardPlugin;
